import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWidget } from './BaseWidget'
import { ClockIcon } from '../Icons'

interface TimeZoneWidgetProps {
  className?: string
  widgetId?: string
}

interface TimeZoneInfo {
  name: string
  timezone: string
  flag: string
  city: string
}

const DEFAULT_TIMEZONES: TimeZoneInfo[] = [
  { name: 'Quebec', timezone: 'America/Toronto', flag: '🇨🇦', city: 'Montreal' },
  { name: 'Vancouver', timezone: 'America/Vancouver', flag: '🇨🇦', city: 'Vancouver' },
  { name: 'New York', timezone: 'America/New_York', flag: '🇺🇸', city: 'New York' },
  { name: 'London', timezone: 'Europe/London', flag: '🇬🇧', city: 'London' },
  { name: 'Paris', timezone: 'Europe/Paris', flag: '🇫🇷', city: 'Paris' },
  { name: 'Tokyo', timezone: 'Asia/Tokyo', flag: '🇯🇵', city: 'Tokyo' },
  { name: 'Dubai', timezone: 'Asia/Dubai', flag: '🇦🇪', city: 'Dubai' },
  { name: 'Bali', timezone: 'Asia/Makassar', flag: '🇮🇩', city: 'Bali' },
]

const TimeZoneWidget: React.FC<TimeZoneWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [currentTime, setCurrentTime] = useState(new Date())
  const [selectedTimezones, setSelectedTimezones] = useState<TimeZoneInfo[]>([
    DEFAULT_TIMEZONES[0], // Quebec (default)
  ])
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const formatTime = (date: Date, timezone: string) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: timezone,
    }).format(date)
  }

  const formatDate = (date: Date, timezone: string) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    }).format(date)
  }

  const getTimeColor = (timezone: string): string => {
    const hour = parseInt(
      new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        hour12: false,
        timeZone: timezone,
      }).format(currentTime)
    )

    // Business hours (9 AM - 6 PM) - Green
    if (hour >= 9 && hour < 18) {
      return 'bg-green-500/40 border-green-300/60 text-white shadow-lg shadow-green-500/20'
    }
    // Early morning/evening (6 AM - 9 AM, 6 PM - 10 PM) - Yellow
    if ((hour >= 6 && hour < 9) || (hour >= 18 && hour < 22)) {
      return 'bg-yellow-500/40 border-yellow-300/60 text-white shadow-lg shadow-yellow-500/20'
    }
    // Night time (10 PM - 6 AM) - Red
    return 'bg-red-500/40 border-red-300/60 text-white shadow-lg shadow-red-500/20'
  }

  const getTimeLabel = (timezone: string): string => {
    const hour = parseInt(
      new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        hour12: false,
        timeZone: timezone,
      }).format(currentTime)
    )

    if (hour >= 9 && hour < 18) return t('dashboard.widgets.businessHours')
    if ((hour >= 6 && hour < 9) || (hour >= 18 && hour < 22)) return t('dashboard.widgets.afterHours')
    return t('dashboard.widgets.nightTime')
  }

  const toggleTimezone = (timezone: TimeZoneInfo) => {
    setSelectedTimezones((prev) => {
      const isSelected = prev.some((tz) => tz.timezone === timezone.timezone)
      if (isSelected) {
        // Don't allow removing if only one left
        if (prev.length === 1) return prev
        return prev.filter((tz) => tz.timezone !== timezone.timezone)
      } else {
        // Don't allow more than 4 timezones
        if (prev.length >= 4) return prev
        return [...prev, timezone]
      }
    })
  }

  const actions = (
    <button
      onClick={() => setShowSettings(!showSettings)}
      className="text-xs bg-white/20 text-white border border-white/30 rounded px-2 py-1 hover:bg-white/30 transition-colors"
    >
      {showSettings ? t('calendar.eventModal.close') : t('dashboard.widgets.selectTimezones')}
    </button>
  )

  return (
    <BaseWidget
      title={t('dashboard.widgets.worldClock')}
      icon={<ClockIcon className="h-5 w-5" />}
      accent="tasks"
      variant="gradient"
      className={className}
      actions={actions}
      minimalDisplay={selectedTimezones.length === 1 && !showSettings}
    >
      <div className="space-y-3">
        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 space-y-2 border border-white/20">
            <p className="text-xs text-white/80 mb-2">
              {t('dashboard.widgets.selectUpToFour')}
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {DEFAULT_TIMEZONES.map((tz) => {
                const isSelected = selectedTimezones.some((selected) => selected.timezone === tz.timezone)
                return (
                  <button
                    key={tz.timezone}
                    onClick={() => toggleTimezone(tz)}
                    className={`text-left px-2 py-1.5 rounded text-xs transition-colors ${
                      isSelected
                        ? 'bg-white/30 text-white border border-white/40'
                        : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <span className="mr-1">{tz.flag}</span>
                    {tz.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Time Zones Display */}
        <div className={`space-y-2 ${selectedTimezones.length === 1 ? 'flex items-center justify-center h-full' : ''}`}>
          {selectedTimezones.map((tz) => (
            <div
              key={tz.timezone}
              className={`rounded-lg border transition-all ${getTimeColor(tz.timezone)} ${
                selectedTimezones.length === 1 ? 'p-4' : 'p-2.5'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className={selectedTimezones.length === 1 ? 'text-2xl' : 'text-base'}>{tz.flag}</span>
                  <span className={`font-semibold text-white ${selectedTimezones.length === 1 ? 'text-base' : 'text-sm'}`}>{tz.name}</span>
                </div>
                <span className={`text-[10px] uppercase font-medium px-2 py-0.5 bg-white/20 rounded-full ${
                  selectedTimezones.length === 1 ? 'hidden' : ''
                }`}>
                  {getTimeLabel(tz.timezone)}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <div className={`font-mono font-bold text-white tabular-nums ${
                  selectedTimezones.length === 1 ? 'text-3xl' : 'text-xl'
                }`}>
                  {formatTime(currentTime, tz.timezone)}
                </div>
                <div className={`text-white/80 ${selectedTimezones.length === 1 ? 'text-sm' : 'text-xs'}`}>
                  {formatDate(currentTime, tz.timezone)}
                </div>
              </div>
              {selectedTimezones.length === 1 && (
                <div className="mt-2 text-center">
                  <span className="text-xs uppercase font-medium px-3 py-1 bg-white/20 rounded-full inline-block">
                    {getTimeLabel(tz.timezone)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Reference Guide - Only show when multiple timezones */}
        {selectedTimezones.length > 1 && !showSettings && (
          <div className="flex items-center justify-center space-x-4 text-[10px] text-white/60 pt-2 border-t border-white/10">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-green-400"></div>
              <span>{t('dashboard.widgets.businessHours')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
              <span>{t('dashboard.widgets.afterHours')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-red-400"></div>
              <span>{t('dashboard.widgets.nightTime')}</span>
            </div>
          </div>
        )}
      </div>
    </BaseWidget>
  )
}

export default TimeZoneWidget

